<template>
  <div :class="modalContentClass">
    <b-tabs class="contact-tabs">
      <overlay-scrollbars :options="{ scrollbars: { autoHide: false }, overflowBehavior: { y: 'scroll', x: 'hidden' } }">
        <b-tab :title="$gettext('Details')" active>
          <b-form ref="contactform" class="tab-form" @submit.prevent="saveForm">
            <div class="modal-form-legend">
              <legend v-translate>Contact details</legend>
            </div>
            <b-form-row>
              <b-col md="4">
                <b-form-group id="input-group-1" label-class="gk-label-margin gk-modal-label" label-for="input-1">
                  <template slot="label">
                    <span v-translate>Name*</span>
                  </template>
                  <b-input-group id="form-input-name">
                    <b-form-input id="input-1" type="text" v-model="contactDetails.name" :required="true" :placeholder="$gettext('My Contact')"></b-form-input>
                  </b-input-group>
                  
                  <GekkoButton 
                    id="import-from-kvk-button"
                    v-if="showImportButton"
                    @click="importFromKVK()"
                    :text="kvkButtonText" 
                    :icon="kvkButtonIcon"
                    variant="link"
                    class="text-info"/>

                    <DropdownContainer 
                      :showContainer="showKvkSuggestionTable" 
                      :preventCloseElementIds="['import-from-kvk-button', 'form-input-name']" 
                      @closeDropdown="onKvkDropdownClose">

                      <KvkSuggestionsTable 
                        ref="kvk_suggestion_table"
                        :companyName="contactDetails.name"
                        @companySelected="onKvkCompanySelected" />
                    </DropdownContainer>

                </b-form-group>
              </b-col>
            
              <b-col md="4">
                <b-form-group id="input-group-2" label-class="gk-label-margin gk-modal-label" label-for="input-2">
                  <template slot="label">
                    <span v-translate>First line contact</span>
                  </template>
                  <b-input-group>
                    <b-form-input id="input-2" type="text" v-model="contactDetails.contact_name" :placeholder="$gettext('John Doe')"></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>
          
              <b-col md="4">
                <b-form-group id="input-group-3" label-class="gk-label-margin gk-modal-label" label-for="input-3">
                  <template slot="label">
                    <span v-translate>Email</span>&nbsp;
                    <font-awesome-icon 
                      id="tt_contacts_email"
                      icon="fa-solid fa-circle-info"
                      class="text-info"
                    />
                    <b-tooltip target="tt_contacts_email" triggers="hover">
                      {{ $gettext('Use \',\' to separate multiple email addresses.') }}
                    </b-tooltip>
                  </template>
                  <b-input-group>
                    <b-form-input id="input-3" type="text" v-model="contactDetails.email" :placeholder="$gettext('john.doe@mycontact.com')"></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-form-row>

            <b-form-row>
              <b-col md="4">
                <b-form-group id="input-group-4" label-class="gk-label-margin gk-modal-label" label-for="input-4">
                  <template slot="label">
                    <span v-translate>Phone number</span>
                  </template>
                  <b-input-group>
                    <b-form-input id="input-4" type="text" v-model="contactDetails.phone_number" :placeholder="$gettext('0123456789')"></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>

              <b-col md="4">
                <b-form-group id="input-group-5" label-class="gk-label-margin gk-modal-label" label-for="input-5">
                  <template slot="label">
                    <label style="margin-bottom: 0px">{{getKvkLabelText}}</label>
                  </template>
                  <b-input-group>
                    <b-form-input id="input-5" type="text" v-model="contactDetails.kvk" :placeholder="$gettext('123456789')"></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>

              <b-col md="4">
                <b-form-group id="input-group-6" label-class="gk-label-margin gk-modal-label" label-for="input-6">
                  <template slot="label">
                    <span v-translate>VAT identification number</span>
                  </template>
                  <b-input-group>
                    <b-form-input id="input-6" type="text" v-model="contactDetails.btw" :placeholder="$gettext('NL123456789B01')"></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-form-row>

            <b-form-row>
              <b-col md="2">
                <b-form-group id="input-group-7" label-for="input-7">
                  <b-form-checkbox
                    id="input-7"
                    :value="true"
                    :unchecked-value="false"
                    v-model="contactDetails.is_customer"
                    switch>
                    <span v-translate>Customer</span>&nbsp;
                    <font-awesome-icon 
                      id="tt_contacts_customer"
                      icon="fa-solid fa-circle-info"
                      class="text-info"
                    />
                    <b-tooltip target="tt_contacts_customer" triggers="hover">
                      {{ $gettext('Mark this contact as a (potential) customer. This contact will then be available for tracking hours and sending invoices and quotations. You can mark this contact as both a supplier and a customer.') }}
                    </b-tooltip>
                    </b-form-checkbox>
                </b-form-group>
              </b-col>

              <b-col md="2">
                <b-form-group id="input-group-8" label-for="input-8">
                  <b-form-checkbox
                    id="input-8"
                    :value="true"
                    :unchecked-value="false"
                    v-model="contactDetails.is_supplier"
                    switch>
                    <span v-translate>Supplier</span>&nbsp;
                      <font-awesome-icon 
                        id="tt_contacts_supplier"
                        icon="fa-solid fa-circle-info"
                        class="text-info"
                      />
                      <b-tooltip target="tt_contacts_supplier" triggers="hover">
                        {{ $gettext('Mark this contact as a (potential) supplier. This contact will then be available for tracking goods and services purchased. You can mark this contact as both a supplier and a customer.') }}
                      </b-tooltip>
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-form-row>

            <hr>

            <div>
              <div class="modal-form-legend">
                <legend v-translate>Address</legend>
              </div>

              <b-form-row>
                <b-col md="4">
                  <b-form-group id="input-group-9" label-class="gk-label-margin gk-modal-label" label-for="input-9">
                    <template slot="label">
                      <span v-translate>Address</span>
                    </template>
                    <b-input-group>
                      <b-form-input id="input-9" type="text" v-model="contactDetails.address" :placeholder="$gettext('Mainstreet 123')"></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </b-col>
          
                <b-col md="4">
                  <b-form-group id="input-group-10" label-class="gk-label-margin gk-modal-label" label-for="input-10">
                    <template slot="label">
                      <span v-translate>Postal code</span>
                    </template>
                    <b-input-group>
                      <b-form-input id="input-10" type="text" v-model="contactDetails.postal_code" placeholder="0000AA"></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </b-col>

                <b-col md="4">
                  <b-form-group id="input-group-11" label-class="gk-label-margin gk-modal-label" label-for="input-11">
                    <template slot="label">
                      <span v-translate>City</span>
                    </template>
                    <b-input-group>
                      <b-form-input id="input-11" type="text" v-model="contactDetails.city" :placeholder="$gettext('Amsterdam')"></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </b-col>
              </b-form-row>
            </div>

            <hr>

            <div cols="3">
              <div class="modal-form-legend">
                <legend v-translate>Country of origin</legend>
              </div>

              <b-form-row>
                <b-col md="4">
                  <b-form-group id="input-group-12" label-class="gk-label-margin gk-modal-label" label-for="input-12">
                    <template slot="label">
                      <span v-translate>Country of origin*</span>&nbsp;
                      <font-awesome-icon 
                        id="tt_contacts_country"
                        icon="fa-solid fa-circle-info"
                        class="text-info"
                      />
                      <b-tooltip target="tt_contacts_country" triggers="hover">
                        {{ $gettext('Gekko uses the country of origin to determine where your customer or supplier is located and suggest how VAT and other taxes should be handled in transactions. Therefore, the country of origin always needs to be filled for any contact. But you can change the settings that the country of origin will not show up on your invoice or in other communications.') }}
                      </b-tooltip>
                    </template>
                    <b-input-group>
                      <b-form-select id="input-12" :options="countries" v-model="contactDetails.country" :required="true" :placeholder="$gettext('Netherlands')"></b-form-select>
                    </b-input-group>
                  </b-form-group>
                </b-col>

                <b-col md="4">
                  <b-form-group id="input-group-13" label-for="input-13" style="padding: 23px 0;">
                    <b-form-checkbox
                      id="input-13"
                      :value="true"
                      :unchecked-value="false"
                      v-model="contactDetails.show_country_on_invoice"
                      switch>
                      <span v-translate>Show country on invoice</span>
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>
              </b-form-row>
            </div>

            <hr>

            <div cols="3">
              <div class="modal-form-legend">
                <legend v-translate>Invoice defaults</legend>
              </div>

              <b-form-row>
                <b-col md="4">
                  <b-form-group id="input-group-15" label-class="gk-label-margin gk-modal-label" label-for="input-15">
                    <template slot="label">
                      <span v-translate>Default hourly rate (Ex. VAT)</span>
                    </template>
                    <b-input-group>
                      <template #append>
                        <b-input-group-text><span>€</span></b-input-group-text>
                      </template>
                      <b-form-input id="input-15" type="number" step=".01" min="0" v-model="contactDetails.hourly_rate" placeholder="0"></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </b-col>

                <b-col md="4">
                  <b-form-group id="input-group-16" label-class="gk-label-margin gk-modal-label" label-for="input-16">
                    <template slot="label">
                      <span v-translate>Default km compensation (Ex. VAT)</span>
                    </template>
                    <b-input-group>
                      <template #append>
                        <b-input-group-text><span>€</span></b-input-group-text>
                      </template>
                      <b-form-input id="input-16" type="number" step=".01" min="0" v-model="contactDetails.default_km_rate" placeholder="0"></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </b-col>

                <b-col md="4">
                  <b-form-group id="input-group-17" label-class="gk-label-margin gk-modal-label" label-for="input-17">
                    <template slot="label">
                      <span v-translate>Payment term</span>
                    </template>
                    <b-input-group>
                      <template #append>
                        <b-input-group-text><translate>days</translate></b-input-group-text>
                      </template>
                      <b-form-input id="input-17" type="number" step="1" min="0" v-model="contactDetails.default_payment_term" placeholder="0"></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </b-col>
              </b-form-row>

              <b-form-row>
                <b-col>
                  <b-form-group id="input-group-14" label-class="gk-label-margin gk-modal-label" label-for="input-14">
                    <template slot="label">
                      <span v-translate>Notes</span>&nbsp;
                      <font-awesome-icon 
                        id="tt_contacts_notes"
                        icon="fa-solid fa-circle-info"
                        class="text-info"
                      />
                      <b-tooltip target="tt_contacts_notes" triggers="hover">
                        {{ $gettext('Contact notes are private and will only ever be visible to you.') }}
                      </b-tooltip>
                    </template>
                    <b-input-group>
                      <b-form-textarea id="input-14" v-model="contactDetails.description" type="text" rows="6"
                      :placeholder="$gettext('Here you can input any details about this customer you want to remember.')"></b-form-textarea>
                    </b-input-group>
                  </b-form-group>
                </b-col>
              </b-form-row>
            </div>
          </b-form>
        </b-tab>

        <b-tab v-if="contactId" :title="$gettext('Documents')" class="m-3">
          <b-card-text>
            <DocumentList />
          </b-card-text>
        </b-tab>
      </overlay-scrollbars>
    </b-tabs>
  </div>
</template>

<script>
import DocumentList from "@/components/contacts/DocumentList";
import CustomerService from "@/store/services/customer.service";
import CompanyService from "@/store/services/company.service";
import GekkoButton from "@/components/GekkoButton";
import KvkSuggestionsTable from "@/components/contacts/KvkSuggestionsTable";
import DropdownContainer from "@/components/ui/DropdownContainer";
import { mapGetters } from "vuex";

export default {
    name: "AddEditContact",
    components: {DocumentList, GekkoButton, KvkSuggestionsTable, DropdownContainer},
    props: {
        contactDetails: {
            type: Object,
            default() {
                return {
                    name: "",
                    contact_name: "",
                    email: "",
                    phone_number: "",
                    kvk: "",
                    btw: "",
                    is_customer: true,
                    is_supplier: false,
                    address: "",
                    postal_code: "",
                    city: "",
                    country: "",
                    show_country_on_invoice: false,
                    description: "",
                    hourly_rate: null,
                    default_km_rate: null,
                    default_payment_term: null,
                    owner_company: window.company_id
                }
            },
            required: true
        },
        is_archived: {
            type: Boolean,
            default: false,
            required: true
        }
    },
    data() {
        return {
            contactId: null,
            countries: [],
            showKvkSuggestionTable: false,
            kvkButtonText: this.$gettext('Import from KVK'),
            kvkButtonIcon: "fa fa-file-import"
        }
    },
    created() {
        this.contactId = this.$route.params['contactId'];
        CompanyService.getCountries().then((response) => {
            this.countries = response.data;
        });
    },
    methods: {
        saveForm() {
            if(!this.$refs.contactform.reportValidity())
                return false
            
            if (this.contactDetails.hourly_rate === "")
              this.contactDetails.hourly_rate = null;
            if (this.contactDetails.default_km_rate === "")
              this.contactDetails.default_km_rate = null;
            if (this.contactDetails.default_payment_term === "")
              this.contactDetails.default_payment_term = null;

            if (this.contactId) {
                CustomerService.update(this.contactId, this.contactDetails).then((response) => {
                    this.$emit('updated', response.data);
                    this.$store.commit('setAlert', {
                        title: this.$gettext("Contact updated"),
                        text: this.$gettext("The contact was updated correctly."),
                        duration: 4
                    });
                }).catch((response) => {
                    let msg = this.$gettext('The contact could not be updated on your contact list.');
                    if (response.email) {
                        msg = this.$gettext('Invalid email address');
                    } else if (response.hourly_rate) {
                        msg = this.$gettext('Invalid hourly rate');
                    } else if (response.default_km_rate) {
                        msg = this.$gettext('Invalid default KM rate');
                    } else if (response.default_payment_term) {
                        msg = this.$gettext('Invalid default payment term');
                    } else if (response.name) {
                        msg = response.name;
                    } else if (response.response) {
                        if (response.response.data) {
                            let firstDictKey = Object.keys(response.response.data)[0];
                            msg = response.response.data[firstDictKey][0];
                        }
                    }
                    this.$store.commit('setAlert', {
                        title: this.$gettext("Error updating contact"),
                        text: msg,
                        variant: "error",
                        duration: 4
                    });
                });
            } else {
                CustomerService.create(this.contactDetails).then((response) => {
                    this.$emit('created', response.data);
                    this.$store.commit('setAlert', {
                        title: this.$gettext("Contact added"),
                        text: this.$gettext("The contact was added to your contact list."),
                        variant: "success",
                        duration: 4
                    });
                }).catch((response) => {
                    let msg = this.$gettext('The contact could not be added to your contact list.');
                    if (response.email) {
                        msg = this.$gettext('Invalid email address');
                    } else if (response.hourly_rate) {
                        msg = this.$gettext('Invalid hourly rate');
                    } else if (response.default_km_rate) {
                        msg = this.$gettext('Invalid default KM rate');
                    } else if (response.default_payment_term) {
                        msg = this.$gettext('Invalid default payment term');
                    } else if (response.name) {
                        msg = response.name;
                    } else if (response.response) {
                        if (response.response.data) {
                            let firstDictKey = Object.keys(response.response.data)[0];
                            msg = response.response.data[firstDictKey][0];
                        }
                    }
                    this.$store.commit('setAlert', {
                        title: this.$gettext("Error adding contact"),
                        text: msg,
                        variant: "error",
                        duration: 4
                    });
                });
            }
        },
        importFromKVK() {
          if (this.$store.getters['getIsPremium']) {
            this.showKvkSuggestionTable = true;
            this.$refs.kvk_suggestion_table.getCompanyList();
            this.kvkButtonText = this.$gettext("Refresh");
            this.kvkButtonIcon = "fa fa-undo";
          } else {
              this.$store.commit('togglePremiumModal', 'web_trips_calculateroute');
          }
        },
        onKvkDropdownClose() {
          this.showKvkSuggestionTable = false;
          this.$refs.kvk_suggestion_table.clearCompanyList();
          this.kvkButtonText = this.$gettext("Import from KVK");
          this.kvkButtonIcon = "fa fa-file-import";
        },
        onKvkCompanySelected(company) {
          this.contactDetails.name = company.handelsnaam;
          this.contactDetails.city = company.plaats;
          this.contactDetails.address = company.straatnaam;
          this.contactDetails.btw = company.vestigingsnummer;
          this.contactDetails.kvk = company.kvkNummer;
          this.contactDetails.country = "NL";

          let params = {url: company.links[0].href};
          CompanyService.getKvkPageByUrl(params).then((response) => {
              if ('hoofdvestiging' in response.data['_embedded'] && 'adressen' in response.data['_embedded']['hoofdvestiging']) {
                  let address = response.data['_embedded']['hoofdvestiging']['adressen'][0];

                  let street_address = '';
                  if (address['straatnaam'])
                      street_address = address['straatnaam'];
                  if (address['huisnummer'])
                      street_address = street_address + ' ' + address['huisnummer'];
                  if (address['huisnummertoevoeging'])
                      street_address = street_address + '-' + address['huisnummertoevoeging'];

                  this.contactDetails.address = street_address;
                  this.contactDetails.postal_code = address['postcode'];
                  this.contactDetails.city = address['plaats'];
              }

              let business_activities = response.data['sbiActiviteiten'][0];
              this.contactDetails.description = business_activities['sbiOmschrijving'];
          });
          
          this.onKvkDropdownClose();
        }
    },
    computed: {
        ...mapGetters({ 
          currentCompany: "main/getCurrentCompany"
        }),
        getKvkLabelText() {
            if (this.contactDetails.country === "ES")
                return this.$gettext("Chamber of commerce number");
            else
                return this.$gettext("KvK number");
        },
        modalContentClass() {
            if (this.contactId)
                return "modal-content";
            else
                return "modal-content-tabs-hidden";
        },
        showImportButton() {
          return !this.contactDetails.kvk.length && this.currentCompany.country === "NL" && this.contactDetails.name.length > 2; // 3 characters required to show button
        }
    }
}
</script>

<style lang="scss" scoped>
#import-from-kvk-span:hover {
  text-decoration: underline;
  cursor: pointer;
}

.tab-form {
    padding-top: 2rem;
    padding-bottom: 2rem;
}
</style>

<style lang="scss">
.modal-content {
    border: none !important;
    box-shadow: none !important;

    .nav-tabs {
        border-bottom: 2px solid #dee2e6;
    }

    .nav-tabs .nav-link.active, .nav-tabs .nav-item.show .nav-link {
        color: #495057 !important;
        background-color: #fff;
        border-top: none;
        border-right: none;
        border-left: none;
        border-bottom: 2px solid $colorBrand !important;
    }
    .nav-tabs .nav-link:hover, .nav-tabs .nav-link:focus {
        color: $colorTextBase;
    }

    .nav-tabs .nav-link {
        margin-bottom: -2px;
        border: none;
        color: #888;
        outline: none;
    }
}

.modal-content-tabs-hidden {
    box-shadow: none !important;

    .nav-tabs {
        display: none !important;
    }
}

.modal-content {
  height: 100%;
  overflow: hidden;
  .contact-tabs {
    height: 100%;
    .tab-content {
      height: 100%;
      .os-host-overflow-y {
        padding-right: 1rem;
      }
    }
  }
}
</style>